import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Download, Check, ArrowRight, Heart, BookOpenCheck, LifeBuoy, Lock, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Reveal from "@/components/site/Reveal";
import { trackLeadConversion } from "@/lib/analytics";
import { metaLead } from "@/lib/metaPixel";
import { getAttribution } from "@/lib/attribution";
import { setUserRole } from "@/lib/userRole";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUNDLE_ID = "pflegekraft-bundle";

const SEO_TITLE = "Pflegekraft-Bundle · Pflegegrad-Spielbuch + Demenz + Sturz · kostenlos | Dickies-Campus";
const SEO_DESC = "Drei kostenlose B2C-Leitfäden in einem Schritt für Pflegekräfte und Angehörige: Pflegegrad-Höherstufung, Demenz-25-Sätze und Sturz-Akutleitfaden – sofort als PDF.";

const ROLES = [
  ["pflegekraft", "Pflegekraft"],
  ["angehoerig",  "Angehörige:r"],
  ["pdl",         "PDL / Leitung"],
  ["andere",      "Andere"],
];

export default function LpPflegekraftBundle() {
  const [form, setForm] = useState({ name: "", email: "", role: "pflegekraft", newsletter: false, phone: "", bundesland: "", company: "", position: "" });
  const [state, setState] = useState({ loading: false, success: false, error: null, response: null });

  useEffect(() => {
    document.title = SEO_TITLE;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = SEO_DESC;
    window.scrollTo(0, 0);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, success: false, error: null, response: null });
    try {
      const res = await axios.post(`${API}/leadmagnet-bundle/${BUNDLE_ID}`, {
        email: form.email,
        name: form.name,
        role: form.role,
        phone: form.phone,
        bundesland: form.bundesland,
        company: form.company,
        position: form.position,
        consent: true,
        newsletter_opt_in: form.newsletter,
        attribution: getAttribution(),
      });
      // DOI: kein direkter Download mehr
      trackLeadConversion(10, `leadmagnet-bundle:${BUNDLE_ID}`);
      metaLead(`leadmagnet-bundle:${BUNDLE_ID}`);
      setUserRole(form.role);
      setState({ loading: false, success: true, error: null, response: res.data });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = (status === 400 && typeof detail === "string") ? detail
        : status === 422 ? "Bitte gültige E-Mail eingeben."
        : "Bitte später erneut versuchen.";
      setState({ loading: false, success: false, error: msg, response: null });
    }
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <Header />

      {/* HERO + FORM */}
      <section className="relative pt-28 md:pt-36 pb-20 md:pb-28 bg-white overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-brand-rose/30 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
          <Reveal>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra" data-testid="pflegekraft-bundle-kicker">
                  Kostenlos · 3 PDFs · 1 E-Mail · Für Pflegekräfte &amp; Angehörige
                </span>
              </div>

              <h1 className="font-display text-[2.4rem] sm:text-5xl lg:text-[3.4rem] text-sand-800 font-normal tracking-tight leading-[1.05]"
                  data-testid="pflegekraft-bundle-h1">
                Drei Werkzeuge <br />
                <span className="text-terra italic">für deine Patient:innen.</span>
              </h1>

              <p className="mt-7 text-sand-700 text-[16px] sm:text-[17px] leading-[1.65] max-w-xl">
                Das Geschenk-Paket für Pflegekräfte (und Angehörige), die ihren Patient:innen
                <b> wirklich</b> helfen wollen &#8211; nicht nur am Bett, sondern auch da, wo es
                ums Pflegegeld, schwierige Demenz-Gespräche und Notfälle geht.
              </p>

              <ul className="mt-8 space-y-3.5 text-[15px] text-sand-700">
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-terra/10 text-terra shrink-0">
                    <Heart className="h-4 w-4" strokeWidth={2} />
                  </span>
                  <span>
                    <span className="font-semibold text-sand-800">Pflegegrad-Spielbuch.</span>{" "}
                    <span className="text-sand-600">Wie du Patient:innen ehrlich in den richtigen Pflegegrad bringst &#8211; 6 NBA-Module, 14-Tage-Plan, Widerspruchs-Mustertext.</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-terra/10 text-terra shrink-0">
                    <BookOpenCheck className="h-4 w-4" strokeWidth={2} />
                  </span>
                  <span>
                    <span className="font-semibold text-sand-800">Demenz · 25 Sätze, die wirklich helfen.</span>{" "}
                    <span className="text-sand-600">Konkrete Formulierungen für die häufigsten Demenz-Situationen &#8211; statt hilfloser Floskeln.</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-terra/10 text-terra shrink-0">
                    <LifeBuoy className="h-4 w-4" strokeWidth={2} />
                  </span>
                  <span>
                    <span className="font-semibold text-sand-800">Sturz · Akutleitfaden.</span>{" "}
                    <span className="text-sand-600">Sofortmaßnahmen, Dokumentation und Angehörigen-Information &#8211; für den Moment, in dem es passiert ist.</span>
                  </span>
                </li>
              </ul>

              <div className="mt-9 flex items-center gap-3 text-[12.5px] text-sand-500">
                <span className="inline-flex h-2 w-2 rounded-full bg-brand-rose animate-pulse" />
                Geschrieben für Pflegekräfte und Angehörige in der häuslichen Pflege.
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="rounded-3xl bg-cream-50 border border-cream-300 shadow-hover p-7 md:p-9" data-testid="pflegekraft-bundle-form-card">
              {!state.success ? (
                <>
                  <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-clay-dark mb-3 flex items-center gap-2">
                    <Lock className="h-3.5 w-3.5" strokeWidth={2} />
                    Pflegekraft-Bundle · 3 PDFs anfordern
                  </div>
                  <h2 className="font-display text-2xl md:text-[1.7rem] text-sand-800 leading-tight">
                    Demenz + Sturz sofort komplett. Spielbuch-Vorschau gratis.
                  </h2>
                  <p className="mt-3 text-[13.5px] text-sand-600 leading-relaxed">
                    Eine E-Mail &#8211; du bekommst die Demenz- und Sturz-Leitfäden <b>komplett</b>
                    und die <b>4-Seiten-Vorschau</b> vom Pflegegrad-Spielbuch sofort. Die volle
                    Spielbuch-Version liegt gratis im Campus.
                  </p>

                  <form onSubmit={submit} className="mt-7 grid gap-4">
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">
                        Du bist …
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {ROLES.map(([v, l]) => (
                          <button
                            key={v} type="button"
                            onClick={() => setForm({ ...form, role: v })}
                            data-testid={`pflegekraft-bundle-role-${v}`}
                            className={`rounded-full px-3 h-10 text-[12.5px] font-semibold border transition-all ${
                              form.role === v
                                ? "border-terra bg-terra text-white"
                                : "border-cream-300 text-sand-700 hover:border-clay bg-white"
                            }`}
                          >{l}</button>
                        ))}
                      </div>
                    </div>

                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Vor- und Nachname *"
                      required minLength={2}
                      data-testid="pflegekraft-bundle-name"
                      className="rounded-2xl border border-cream-300 bg-white px-5 h-12 text-[15px] outline-none focus:border-terra"
                    />
                    <input
                      required type="email" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="E-Mail-Adresse *"
                      data-testid="pflegekraft-bundle-email"
                      className="rounded-2xl border border-cream-300 bg-white px-5 h-12 text-[15px] outline-none focus:border-terra"
                    />
                    <input
                      required type="tel" inputMode="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="Telefon (z. B. 0211 12345678) *"
                      data-testid="pflegekraft-bundle-phone"
                      className="rounded-2xl border border-cream-300 bg-white px-5 h-12 text-[15px] outline-none focus:border-terra"
                    />
                    <select
                      required value={form.bundesland}
                      onChange={(e) => setForm({ ...form, bundesland: e.target.value })}
                      data-testid="pflegekraft-bundle-bundesland"
                      className="rounded-2xl border border-cream-300 bg-white px-5 h-12 text-[15px] outline-none focus:border-terra"
                    >
                      <option value="">Bundesland * (bitte wählen)</option>
                      <option value="BW">Baden-Württemberg</option>
                      <option value="BY">Bayern</option>
                      <option value="BE">Berlin</option>
                      <option value="BB">Brandenburg</option>
                      <option value="HB">Bremen</option>
                      <option value="HH">Hamburg</option>
                      <option value="HE">Hessen</option>
                      <option value="MV">Mecklenburg-Vorpommern</option>
                      <option value="NI">Niedersachsen</option>
                      <option value="NW">Nordrhein-Westfalen</option>
                      <option value="RP">Rheinland-Pfalz</option>
                      <option value="SL">Saarland</option>
                      <option value="SN">Sachsen</option>
                      <option value="ST">Sachsen-Anhalt</option>
                      <option value="SH">Schleswig-Holstein</option>
                      <option value="TH">Thüringen</option>
                      <option value="AT">Österreich</option>
                      <option value="CH">Schweiz</option>
                      <option value="OTHER">Anderes</option>
                    </select>

                    <label className="flex items-start gap-3 text-[13px] text-sand-600 leading-snug cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={form.newsletter}
                        onChange={(e) => setForm({ ...form, newsletter: e.target.checked })}
                        data-testid="pflegekraft-bundle-newsletter"
                        className="mt-1 h-4 w-4 accent-terra shrink-0"
                      />
                      <span>
                        <b>Helden-Briefing abonnieren</b> &#8211; 1× pro Monat Pflege-Wissen kompakt. Abmelden jederzeit.
                      </span>
                    </label>

                    <Button
                      type="submit"
                      disabled={state.loading}
                      data-testid="pflegekraft-bundle-submit"
                      className="rounded-full bg-terra hover:bg-terra-dark text-white h-12 px-6 text-[15px] font-semibold mt-1"
                    >
                      {state.loading ? "Wird gesendet …" : (
                        <span className="inline-flex items-center gap-2">
                          <Download className="h-4 w-4" strokeWidth={1.8} />
                          Alle 3 PDFs per E-Mail anfordern
                        </span>
                      )}
                    </Button>

                    {state.error && (
                      <p className="text-[13px] text-terra font-medium" data-testid="pflegekraft-bundle-error">
                        {state.error}
                      </p>
                    )}
                    <p className="text-[11px] text-sand-500 leading-snug">
                      Mit dem Absenden stimmst du der Verarbeitung deiner Daten gemäß{" "}
                      <Link to="/datenschutz" className="underline">Datenschutzerklärung</Link> zu.
                      Wir senden dir eine Bestätigungs-Mail — erst nach Klick bekommst du die PDFs.
                    </p>
                  </form>
                </>
              ) : (
                <div data-testid="pflegekraft-bundle-success" className="py-2">
                  <div className="flex flex-col items-center text-center">
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-rose/30 text-brand-darkred">
                      <Check className="h-7 w-7" strokeWidth={2} />
                    </div>
                    <h3 className="mt-5 font-display text-2xl text-sand-800">Fast geschafft!</h3>
                    <p className="mt-3 text-sand-600 text-[14px] leading-relaxed max-w-md">
                      Wir haben dir gerade eine E-Mail an <strong className="text-sand-800">{form.email}</strong> geschickt.
                      Bitte klicke darin auf den Bestätigungs-Link — danach bekommst du alle 3 PDFs sofort.
                    </p>
                    <p className="mt-2 text-[12px] text-sand-500">
                      Keine Mail erhalten? Schau bitte auch im Spam-Ordner.
                    </p>
                  </div>

                  <div className="mt-6 rounded-2xl bg-brand-darkred text-white p-6">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-brand-rose mb-3 flex items-center gap-2">
                      <Lock className="h-3.5 w-3.5" strokeWidth={2} />
                      Spielbuch-Vollversion im Campus
                    </div>
                    <p className="text-[14.5px] leading-relaxed">
                      Die <b>komplette Pflegegrad-Spielbuch-Version</b> (15 Seiten, alle 6 NBA-Module
                      mit Stolperstellen, vollständiger 14-Tage-Plan, Widerspruchs-Mustertext, Sonderfälle)
                      liegt im Dickies-Campus &#8212; Zugang für Mitglieder.
                    </p>
                    <Link to="/preise"
                          data-testid="pflegekraft-bundle-register-cta"
                          className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-brand-darkred px-5 h-11 text-[14px] font-semibold hover:bg-brand-rose/30 transition">
                      Mitglied werden <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* QUOTE */}
      <section className="bg-brand-rose py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <blockquote className="font-display italic text-2xl md:text-[1.85rem] text-brand-darkred leading-[1.4] text-center">
              „Endlich PDFs, die mir helfen &#8211; nicht nur den Pflegedienst. Das Spielbuch hat meiner Mutter einen ganzen Pflegegrad gebracht."
            </blockquote>
            <p className="mt-7 text-center text-white text-[13px] uppercase tracking-[0.22em] font-semibold">
              Tochter eines Pflegebedürftigen · NRW (anonymisiert)
            </p>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
